import { Fragment, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { Bars3BottomLeftIcon, BellIcon } from "@heroicons/react/24/outline";
import { signOut } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";

import avatar from "../../../assets/avatar.png";

import clsx from "clsx";

import useTickets from "../../../data/Ticket/useTickets";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../Notifications/useNotifications";
import TicketActivityDrawer, {
  ticketAttentionBadgeCount,
} from "../TicketActivityDrawer/TicketActivityDrawer";

export interface TopbarProps {
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Topbar({ setSidebarOpen }: TopbarProps) {
  const [ticketActivityOpen, setTicketActivityOpen] = useState(false);
  const { tickets } = useTickets({ itemsPerPage: 200, includeNotes: true });
  const { auth, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  const ticketAttentionCount = useMemo(
    () => ticketAttentionBadgeCount(tickets.data?.data, user?.email),
    [tickets.data, user?.email],
  );

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      // Drop cached data so the next sign in never sees the previous user's.
      queryClient.clear();
      navigate("/login");
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to sign out",
      });
    }
  };

  return (
    <>
    <div className="sticky top-0 flex h-16 flex-shrink-0 bg-white shadow z-1">
      <button
        type="button"
        className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500 md:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      <div className="flex flex-1 justify-between px-4">
        <div className="flex flex-1" />
        <div className="ml-4 flex items-center md:ml-6">
          <button
            type="button"
            className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            onClick={() => setTicketActivityOpen(true)}
            aria-label="Ticket activity"
          >
            <span className="sr-only">View ticket activity</span>
            <BellIcon className="h-6 w-6" aria-hidden="true" />
            {ticketAttentionCount > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-pink-600 px-1 text-[10px] font-bold leading-none text-white">
                {ticketAttentionCount > 9 ? "9+" : ticketAttentionCount}
              </span>
            ) : null}
          </button>

          {/* Profile dropdown */}
          <Menu as="div" className="relative ml-3">
            <div>
              <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
                <span className="sr-only">Open user menu</span>
                {/* Prefer the signed in user's own picture, falling back to the placeholder. */}
                <img
                  className="h-8 w-8 rounded-full"
                  src={user?.photoURL ?? avatar}
                  alt={user?.displayName ?? user?.email ?? ""}
                />
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {user?.email ? (
                  <div className="border-b px-4 py-2">
                    <p className="truncate text-xs text-gray-500">
                      {user.email}
                    </p>
                  </div>
                ) : null}

                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={clsx(
                        active ? "bg-gray-100" : "",
                        "block px-4 py-2 text-sm text-gray-700",
                      )}
                    >
                      Your Profile
                    </Link>
                  )}
                </Menu.Item>

                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className={clsx(
                        active ? "bg-gray-100" : "",
                        "block w-full px-4 py-2 text-left text-sm text-gray-700",
                      )}
                    >
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
    <TicketActivityDrawer
      open={ticketActivityOpen}
      onClose={() => setTicketActivityOpen(false)}
    />
    </>
  );
}
